import {
  normalize,
  historyDate,
  isStatusReached,
  offeredDate,
  operationDate,
  customerCessionDate,
  paymentDate,
  debtDaysText,
  hasEvaluationRequest,
  hasEvaluationDocumentReview,
  hasDocumentReview,
  hasRatification,
  BASE_STATUS_RANK,
} from './invoiceTrackingHelpers';

// A step is completed once its `completeStatus` is reached by the invoice's current status
// (monotonic by rank). The first not-completed step is `active` (unless its `isActive` guard
// fails, e.g. evaluation hasn't started); the rest are `pending`.
const computeStates = (invoice, configs, statusRank) => {
  let lastFinished = -1;
  configs.forEach((config, index) => {
    if (isStatusReached(invoice, config.completeStatus, statusRank)) lastFinished = index;
  });
  return configs.map((config, index) => {
    if (index <= lastFinished) return 'finished';
    const isCurrent = index === lastFinished + 1;
    const canActivate = !config.isActive || config.isActive(invoice);
    if (isCurrent && canActivate) return 'active';
    return 'pending';
  });
};

const stepLabel = (config, state) => {
  if (state === 'finished') return config.doneLabel;
  if (state === 'active') return config.activeLabel;
  return config.pendingLabel;
};

const defaultBuild = (config, invoice, state, statusRank) => ({
  key: config.key,
  label: stepLabel(config, state),
  state,
  datetime: state === 'finished' && config.getDate ? config.getDate(invoice) : '',
  // Bullets only render on reached steps (active/finished), never on a pending one.
  ...(config.getBullets && state !== 'pending'
    ? { bullets: config.getBullets(invoice, statusRank) }
    : {}),
});

export const buildTrackingSteps = (invoice, stepConfigs, statusRank = BASE_STATUS_RANK) => {
  if (!invoice) return [];
  const visible = stepConfigs.filter((config) => !config.isVisible || config.isVisible(invoice));
  const states = computeStates(invoice, visible, statusRank);
  return visible.map((config, index) => (
    config.build
      ? config.build(invoice, states[index])
      : defaultBuild(config, invoice, states[index], statusRank)
  ));
};

export const EVALUATION_STEP = {
  key: 'evaluation',
  pendingLabel: 'Evaluación',
  activeLabel: 'En evaluación',
  doneLabel: 'Aprobación de factura',
  completeStatus: 'OFFERED',
  isActive: hasEvaluationRequest,
  getDate: offeredDate,
};

export const OPERATION_STEP = {
  key: 'operation',
  pendingLabel: 'Operación',
  activeLabel: 'Pendiente de operación',
  doneLabel: 'Operación de factura',
  completeStatus: 'PROCESSING',
  getDate: operationDate,
};

export const CESSION_STEP = {
  key: 'cession',
  pendingLabel: 'Cesión',
  activeLabel: 'Procesando cesión',
  doneLabel: 'Factura cedida',
  completeStatus: 'PENDINGTRANSFER',
  getDate: customerCessionDate,
};

export const MONEY_TRANSFER_STEP = {
  key: 'moneyTransfer',
  pendingLabel: 'Giro',
  activeLabel: 'En curse',
  doneLabel: 'Girada',
  completeStatus: 'TRANSFERED',
  getDate: (invoice) => historyDate(invoice, 'TRANSFERED'),
};

const paymentActiveLabel = (invoice) => {
  const status = normalize(invoice.status);
  if (status === 'DEBT') return 'En mora';
  if (status === 'PARTIALCOLLECTION') return 'Parcialmente recaudada';
  return 'En cobranza';
};

export const PAYMENT_STEP = {
  key: 'payment',
  completeStatus: 'FINISHED',
  build: (invoice, state) => {
    if (state === 'finished') {
      return {
        key: 'payment',
        label: 'Recaudada',
        state,
        datetime: paymentDate(invoice),
        extra: debtDaysText(invoice),
      };
    }
    if (state === 'pending') {
      return { key: 'payment', label: 'Pago', state };
    }
    return {
      key: 'payment',
      label: paymentActiveLabel(invoice),
      state,
      extra: normalize(invoice.status) === 'DEBT' ? debtDaysText(invoice) : '',
    };
  },
};

const requirementBullet = ({
  isVisible, activeText, rejectedStatus, rejectedText, doneText, isDone,
}) => (invoice, statusRank) => {
  if (!isVisible(invoice)) return null;
  const status = normalize(invoice.status);
  if (rejectedStatus && status === normalize(rejectedStatus)) {
    return { text: rejectedText, tone: 'error' };
  }
  if (isDone(invoice, statusRank)) return { text: doneText, tone: 'default' };
  return { text: activeText, tone: 'default' };
};

const reaches = (status) => (invoice, statusRank) => isStatusReached(invoice, status, statusRank);

export const riskReviewBullet = requirementBullet({
  isVisible: hasEvaluationDocumentReview,
  activeText: 'Revisando pendientes de riesgo',
  rejectedStatus: 'PENDINGDOCUMENTSEXPIRED',
  rejectedText: 'Pendientes de riesgo expirados',
  doneText: 'Pendientes de riesgo revisados',
  isDone: reaches('OFFERED'),
});

export const documentReviewBullet = requirementBullet({
  isVisible: hasDocumentReview,
  activeText: 'Revisando documentos',
  rejectedStatus: 'REJECTEDDOCUMENTS',
  rejectedText: 'Rechazada en revisión de documentos',
  doneText: 'Documentos revisados',
  isDone: (invoice) => normalize(invoice.status) !== 'PENDINGDOCUMENTSREVIEW',
});

export const ratificationReviewBullet = requirementBullet({
  isVisible: hasRatification,
  activeText: 'Pendiente de ratificación',
  rejectedStatus: 'REJECTEDRATIFICATION',
  rejectedText: 'Rechazada en ratificación',
  doneText: 'Ratificada',
  isDone: (invoice) => normalize(invoice.status) !== 'PENDINGRATIFICATION',
});
